import type {
  AchievementClaimResponse,
  AchievementsResponse,
  AdRewardResponse,
  AuthResponse,
  BattlePassClaimAllResponse,
  BattlePassClaimResponse,
  BattlePassPremiumResponse,
  BattlePassState,
  BootCampRewardResponse,
  BuyRequest,
  BuyResponse,
  DevLabRequest,
  DevStatusResponse,
  CatalogResponse,
  CheckoutRequest,
  CheckoutResponse,
  ClaimTierRequest,
  ClanDetail,
  ClanJoinResponse,
  ClanListResponse,
  CreateClanRequest,
  CreateRoomRequest,
  DailyLoginResponse,
  FriendRequest,
  ForgotPasswordRequest,
  FriendsResponse,
  GuestAuthRequest,
  HealthResponse,
  InboxClaimResponse,
  InboxResponse,
  JoinRoomRequest,
  LeaderboardId,
  LeaderboardResponse,
  LoginRequest,
  MatchAssignment,
  MatchHistoryResponse,
  MeResponse,
  ModeId,
  NameSuggestionsResponse,
  OkResponse,
  Party,
  PlayerHighlight,
  PlayerHighlightsResponse,
  PartyResponse,
  PublicProfile,
  PublicRoomsResponse,
  QuestClaimResponse,
  QuestsResponse,
  QuickPlayRequest,
  QuickPlayResponse,
  RegisterRequest,
  ReportRequest,
  ResetPasswordRequest,
  ResetPasswordResponse,
  SaveSkinRequest,
  TelemetryBatch,
  UpdateClanRequest,
  UpdateCosmeticsRequest,
  UpdateLoadoutRequest,
  UpdateMeRequest,
  VerifyPurchaseRequest,
  VerifyPurchaseResponse,
} from '@vm/shared';
import { request, requestBytes } from './http';

/** Fields a response may carry that we merge straight into the profile. */
export type ProfilePatch = Partial<Pick<MeResponse, 'wallet' | 'inventory' | 'loadout' | 'cosmetics' | 'progression' | 'customSkins' | 'unreadInbox' | 'name' | 'renamed' | 'tutorialDone'>> & {
  me?: MeResponse;
};

const id = encodeURIComponent;

/**
 * Typed client for every REST route of the API contract (packages/shared/src/api/types.ts).
 * Every /v1/me mutation answers with the full updated profile (UpdateMeResponse = MeResponse).
 */
export const api = {
  health: () => request<HealthResponse>('GET', '/health', undefined, { auth: false, timeoutMs: 5000 }),

  auth: {
    guest: (req: GuestAuthRequest) => request<AuthResponse>('POST', '/v1/auth/guest', req, { auth: false }),
    /** Links an email + password to the signed-in guest account. */
    register: (req: RegisterRequest) => request<AuthResponse>('POST', '/v1/auth/register', req),
    login: (req: LoginRequest) => request<AuthResponse>('POST', '/v1/auth/login', req, { auth: false }),
    logout: (refreshToken: string | null) => request<OkResponse>('POST', '/v1/auth/logout', refreshToken ? { refreshToken } : {}, { timeoutMs: 5000 }),
    /** Emails a link to choose a new password (the answer never says whether the email has an account). */
    forgotPassword: (req: ForgotPasswordRequest) => request<OkResponse>('POST', '/v1/auth/forgot-password', req, { auth: false }),
    resetPassword: (req: ResetPasswordRequest) => request<ResetPasswordResponse>('POST', '/v1/auth/reset-password', req, { auth: false }),
  },

  me: {
    get: () => request<MeResponse>('GET', '/v1/me'),
    update: (req: UpdateMeRequest) => request<MeResponse>('PATCH', '/v1/me', req),
    remove: () => request<OkResponse>('DELETE', '/v1/me'),
    setLoadout: (req: UpdateLoadoutRequest) => request<MeResponse>('PUT', '/v1/me/loadout', req),
    setCosmetics: (req: UpdateCosmeticsRequest) => request<MeResponse>('PUT', '/v1/me/cosmetics', req),
    saveSkin: (slot: number, req: SaveSkinRequest) => request<MeResponse>('PUT', `/v1/me/skins/${slot}`, req),
    deleteSkin: (slot: number) => request<MeResponse>('DELETE', `/v1/me/skins/${slot}`),
    nameSuggestions: () => request<NameSuggestionsResponse>('GET', '/v1/me/name-suggestions'),
    matches: (limit = 10) => request<MatchHistoryResponse>('GET', `/v1/me/matches?limit=${limit}`),
  },

  users: {
    get: (userId: string) => request<PublicProfile>('GET', `/v1/users/${id(userId)}`),
    block: (userId: string) => request<OkResponse>('POST', `/v1/users/${id(userId)}/block`),
    unblock: (userId: string) => request<OkResponse>('DELETE', `/v1/users/${id(userId)}/block`),
  },

  catalog: () => request<CatalogResponse>('GET', '/v1/catalog', undefined, { auth: false }),

  shop: {
    buy: (req: BuyRequest) => request<BuyResponse>('POST', '/v1/shop/buy', req),
  },

  dev: {
    status: () => request<DevStatusResponse>('GET', '/v1/dev/status'),
    lab: (req: DevLabRequest) => request<MeResponse>('POST', '/v1/dev/lab', req),
  },

  iap: {
    checkout: (req: CheckoutRequest) => request<CheckoutResponse>('POST', '/v1/iap/checkout', req),
    verify: (req: VerifyPurchaseRequest) => request<VerifyPurchaseResponse>('POST', '/v1/iap/verify', req),
  },

  rewards: {
    daily: () => request<DailyLoginResponse>('POST', '/v1/rewards/daily', {}),
    bootcamp: () => request<BootCampRewardResponse>('POST', '/v1/rewards/bootcamp', {}),
    ad: (placement: string) => request<AdRewardResponse>('POST', '/v1/rewards/ad', { placement }),
  },

  battlepass: {
    get: () => request<BattlePassState>('GET', '/v1/battlepass'),
    claim: (req: ClaimTierRequest) => request<BattlePassClaimResponse>('POST', '/v1/battlepass/claim', req),
    claimAll: () => request<BattlePassClaimAllResponse>('POST', '/v1/battlepass/claim-all', {}),
    buyPremium: () => request<BattlePassPremiumResponse>('POST', '/v1/battlepass/premium', {}),
  },

  quests: {
    get: () => request<QuestsResponse>('GET', '/v1/quests'),
    claim: (questId: string) => request<QuestClaimResponse>('POST', `/v1/quests/${id(questId)}/claim`, {}),
    reroll: (questId: string) => request<QuestsResponse>('POST', `/v1/quests/${id(questId)}/reroll`, {}),
  },

  achievements: {
    get: () => request<AchievementsResponse>('GET', '/v1/achievements'),
    claim: (achievementId: string) => request<AchievementClaimResponse>('POST', `/v1/achievements/${id(achievementId)}/claim`, {}),
  },

  leaderboards: {
    get: (board: LeaderboardId, period: 'week' | 'all', limit = 50) => request<LeaderboardResponse>('GET', `/v1/leaderboards/${board}?period=${period}&limit=${limit}`),
  },

  friends: {
    get: () => request<FriendsResponse>('GET', '/v1/friends'),
    request: (req: FriendRequest) => request<FriendsResponse>('POST', '/v1/friends/request', req),
    accept: (userId: string) => request<FriendsResponse>('POST', `/v1/friends/${id(userId)}/accept`, {}),
    decline: (userId: string) => request<FriendsResponse>('POST', `/v1/friends/${id(userId)}/decline`, {}),
    remove: (userId: string) => request<FriendsResponse>('DELETE', `/v1/friends/${id(userId)}`),
  },

  reports: {
    create: (req: ReportRequest) => request<OkResponse>('POST', '/v1/reports', req),
  },

  clans: {
    search: (query: string, limit = 30) => request<ClanListResponse>('GET', `/v1/clans?query=${id(query)}&limit=${limit}`),
    create: (req: CreateClanRequest) => request<ClanDetail>('POST', '/v1/clans', req),
    get: (clanId: string) => request<ClanDetail>('GET', `/v1/clans/${id(clanId)}`),
    update: (clanId: string, req: UpdateClanRequest) => request<ClanDetail>('PATCH', `/v1/clans/${id(clanId)}`, req),
    disband: (clanId: string) => request<OkResponse>('DELETE', `/v1/clans/${id(clanId)}`),
    join: (clanId: string) => request<ClanJoinResponse>('POST', `/v1/clans/${id(clanId)}/join`, {}),
    leave: (clanId: string) => request<OkResponse>('POST', `/v1/clans/${id(clanId)}/leave`, {}),
    acceptRequest: (clanId: string, userId: string) => request<ClanDetail>('POST', `/v1/clans/${id(clanId)}/requests/${id(userId)}/accept`, {}),
    declineRequest: (clanId: string, userId: string) => request<ClanDetail>('POST', `/v1/clans/${id(clanId)}/requests/${id(userId)}/decline`, {}),
    promote: (clanId: string, userId: string) => request<ClanDetail>('POST', `/v1/clans/${id(clanId)}/members/${id(userId)}/promote`, {}),
    demote: (clanId: string, userId: string) => request<ClanDetail>('POST', `/v1/clans/${id(clanId)}/members/${id(userId)}/demote`, {}),
    kick: (clanId: string, userId: string) => request<ClanDetail>('POST', `/v1/clans/${id(clanId)}/members/${id(userId)}/kick`, {}),
  },

  inbox: {
    get: () => request<InboxResponse>('GET', '/v1/inbox'),
    read: (msgId: string) => request<InboxResponse>('POST', `/v1/inbox/${id(msgId)}/read`, {}),
    claim: (msgId: string) => request<InboxClaimResponse>('POST', `/v1/inbox/${id(msgId)}/claim`, {}),
    remove: (msgId: string) => request<InboxResponse>('DELETE', `/v1/inbox/${id(msgId)}`),
  },

  play: {
    quick: (req: QuickPlayRequest) => request<QuickPlayResponse>('POST', '/v1/play/quick', req),
    cancel: () => request<OkResponse>('POST', '/v1/play/cancel', {}),
  },

  rooms: {
    create: (req: CreateRoomRequest) => request<MatchAssignment>('POST', '/v1/rooms', req),
    join: (req: JoinRoomRequest) => request<MatchAssignment>('POST', '/v1/rooms/join', req),
    listPublic: () => request<PublicRoomsResponse>('GET', '/v1/rooms/public'),
  },

  party: {
    get: () => request<PartyResponse>('GET', '/v1/party'),
    /** Creates a party, or sets its mode when you already lead one. */
    create: (mode?: ModeId | null) => request<Party>('POST', '/v1/party', mode === undefined ? {} : { mode }),
    invite: (userId: string) => request<Party>('POST', '/v1/party/invite', { userId }),
    join: (partyId: string) => request<Party>('POST', '/v1/party/join', { partyId }),
    leave: () => request<OkResponse>('POST', '/v1/party/leave', {}),
    kick: (userId: string) => request<Party>('POST', '/v1/party/kick', { userId }),
    ready: (ready: boolean) => request<Party>('POST', '/v1/party/ready', { ready }),
  },

  events: {
    send: (batch: TelemetryBatch) => request<OkResponse>('POST', '/v1/events', batch, { keepalive: true, timeoutMs: 8000 }),
  },

  /** Highlight replays of matches the player was in (Play of the Game) and sharing their own. */
  highlights: {
    ofMatch: (matchId: string) => request<PlayerHighlightsResponse>('GET', `/v1/highlights/match/${id(matchId)}`),
    mine: () => request<PlayerHighlightsResponse>('GET', '/v1/highlights/mine'),
    /** The anonymized replay (gzip). */
    replay: (hid: string) => requestBytes('GET', `/v1/highlights/${id(hid)}/replay`),
    share: (hid: string) => request<PlayerHighlight>('POST', `/v1/highlights/${id(hid)}/share`, {}),
    poster: (hid: string, jpeg: Blob) => requestBytes('PUT', `/v1/highlights/${id(hid)}/poster`, jpeg),
  },
};
